<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import type { Graduation, Laboratory, ProductWithDetails } from '../../types';

const props = defineProps<{
  show: boolean;
  graduations: Record<string, Graduation[]>;
  laboratories: Laboratory[];
  product: ProductWithDetails;
}>();

const emit = defineEmits<{
  'close': [];
}>();

// Estado para el laboratorio seleccionado
const selectedLaboratory = ref('');
const sphericalMode = ref<'positive' | 'negative'>('positive');

// Inicializar el laboratorio seleccionado cuando cambian los props
watch(() => props.laboratories, (newLabs) => {
  if (newLabs.length > 0 && !selectedLaboratory.value) {
    selectedLaboratory.value = newLabs[0].id || '';
  }
}, { immediate: true });

// Graduaciones del laboratorio seleccionado
const activeGraduations = computed(() => {
  return props.graduations[selectedLaboratory.value] || [];
});

// Obtener laboratorio activo
const activeLaboratory = computed(() => {
  return props.laboratories.find(lab => lab.id === selectedLaboratory.value);
});

// Calcular precios
const prices = computed(() => {
  if (!activeLaboratory.value) return { pair: 0, unit: 0 };

  const labDetails = props.product.laboratories.find(
    lab => lab.laboratory.id === activeLaboratory.value.id
  );

  if (!labDetails) return { pair: 0, unit: 0 };

  const pairPrice = Math.round(labDetails.details.PrecioDeCosto * props.product.Factor);
  return {
    pair: pairPrice,
    unit: Math.round(pairPrice / 2)
  };
});

// Calcular rangos para el laboratorio activo
const ranges = computed(() => {
  if (!activeGraduations.value.length) return null;

  const esfericos = activeGraduations.value.map(g => g.esferico);
  const cilindricos = activeGraduations.value.map(g => g.cilindrico);

  return {
    esferico: {
      min: Math.min(...esfericos),
      max: Math.max(...esfericos)
    },
    cilindrico: {
      min: Math.min(...cilindricos),
      max: Math.max(...cilindricos)
    }
  };
});

// Generar rangos de valores para la grilla
const sphericalRange = computed(() => {
  if (!ranges.value) return [];
  const values: number[] = [];
  
  if (sphericalMode.value === 'positive') {
    // Para valores positivos, empezar desde 0 hacia arriba
    for (let i = 0; i <= ranges.value.esferico.max; i += 0.25) {
      values.push(Number(i.toFixed(2)));
    }
  } else {
    // Para valores negativos, empezar desde 0 hacia abajo
    for (let i = 0; i >= ranges.value.esferico.min; i -= 0.25) {
      values.push(Number(i.toFixed(2)));
    }
  }
  
  return values;
});

const cylindricalRange = computed(() => {
  if (!ranges.value) return [];
  const values: number[] = [];
  for (let i = 0; i >= ranges.value.cilindrico.min; i -= 0.25) {
    values.push(Number(i.toFixed(2)));
  }
  return values;
});

// Verificar si una graduación está disponible
const isAvailable = (spherical: number, cylindrical: number) => {
  return activeGraduations.value.some(g => 
    g.esferico === spherical && g.cilindrico === cylindrical
  );
};

// Formatear graduación para tooltip
const formatGraduation = (spherical: number, cylindrical: number) => {
  const sphericalStr = spherical > 0 ? `+${spherical.toFixed(2)}` : spherical.toFixed(2);
  const cylindricalStr = cylindrical > 0 ? `+${cylindrical.toFixed(2)}` : cylindrical.toFixed(2);
  return `Esférico: ${sphericalStr} / Cilíndrico: ${cylindricalStr}`;
};

// Formatear número con signo
const formatNumber = (value: number) => {
  return value > 0 ? `+${value.toFixed(2)}` : value.toFixed(2);
};

// Estado para el desplazamiento con mouse
const isDragging = ref(false);
const startX = ref(0);
const startY = ref(0);
const scrollLeft = ref(0);
const scrollTop = ref(0);
const tableContainer = ref<HTMLElement | null>(null);

// Manejadores de eventos para el desplazamiento con mouse
const handleMouseDown = (e: MouseEvent) => {
  if (!tableContainer.value) return;
  
  isDragging.value = true;
  startX.value = e.pageX - tableContainer.value.offsetLeft;
  startY.value = e.pageY - tableContainer.value.offsetTop;
  scrollLeft.value = tableContainer.value.scrollLeft;
  scrollTop.value = tableContainer.value.scrollTop;
  
  tableContainer.value.style.cursor = 'grabbing';
  tableContainer.value.style.userSelect = 'none';
};

const handleMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !tableContainer.value) return;

  e.preventDefault();
  const x = e.pageX - tableContainer.value.offsetLeft;
  const y = e.pageY - tableContainer.value.offsetTop;
  const walkX = (x - startX.value) * 1.5;
  const walkY = (y - startY.value) * 1.5;
  
  tableContainer.value.scrollLeft = scrollLeft.value - walkX;
  tableContainer.value.scrollTop = scrollTop.value - walkY;
};

const handleMouseUp = () => {
  isDragging.value = false;
  if (tableContainer.value) {
    tableContainer.value.style.cursor = 'grab';
    tableContainer.value.style.removeProperty('user-select');
  }
};
</script>

<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex min-h-screen items-center justify-center p-4">
      <!-- Backdrop -->
      <div 
        class="fixed inset-0 bg-black/50 transition-opacity" 
        @click="emit('close')"
      />
      
      <!-- Modal - 80% width and height -->
      <div class="relative bg-white rounded-lg shadow-xl w-[80%] h-[80vh] flex flex-col mx-auto">
        <!-- Header -->
        <div class="flex items-center justify-between p-4 border-b">
          <h2 class="text-lg font-semibold text-slate-800">
            Graduaciones Disponibles - {{ product.Nombre }}
          </h2>
          <button
            @click="emit('close')"
            class="text-slate-400 hover:text-slate-500"
          >
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Laboratory selector and prices -->
        <div class="p-4 border-b">
          <div class="flex items-center justify-between">
            <!-- Laboratory selector -->
            <div class="w-64">
              <select
                v-model="selectedLaboratory"
                class="input-field text-sm"
              >
                <option v-for="lab in laboratories" :key="lab.id" :value="lab.id">
                  {{ lab.Nombre }}
                </option>
              </select>
            </div>

            <!-- Price information -->
            <div v-if="activeLaboratory" class="flex items-center gap-8">
              <div class="text-right">
                <span class="text-sm text-slate-600">Par:</span>
                <span class="text-sm font-medium ml-2">
                  ${{ prices.pair.toLocaleString() }}
                </span>
              </div>
              <div class="text-right">
                <span class="text-sm text-slate-600">Unidad:</span>
                <span class="text-sm font-medium ml-2">
                  ${{ prices.unit.toLocaleString() }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Content - Flex grow to fill available space -->
        <div class="flex-1 p-4 overflow-hidden flex flex-col">
          <!-- Resumen de rangos -->
          <div v-if="ranges" class="mb-4 bg-slate-50 p-3 rounded text-sm">
            <div class="grid grid-cols-2 gap-4">
              <div>
                <span class="text-slate-600">Esférico: </span>
                <span class="font-medium">
                  {{ formatNumber(ranges.esferico.min) }} a {{ formatNumber(ranges.esferico.max) }}
                </span>
              </div>
              <div>
                <span class="text-slate-600">Cilíndrico: </span>
                <span class="font-medium">
                  {{ formatNumber(ranges.cilindrico.min) }} a {{ formatNumber(ranges.cilindrico.max) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Toggle buttons for positive/negative -->
          <div class="flex gap-2 mb-4">
            <button
              @click="sphericalMode = 'positive'"
              :class="[
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                sphericalMode === 'positive'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              ]"
            >
              Valores Positivos
            </button>
            <button
              @click="sphericalMode = 'negative'"
              :class="[
                'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                sphericalMode === 'negative'
                  ? 'bg-indigo-500 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              ]"
            >
              Valores Negativos
            </button>
          </div>

          <!-- Grilla de graduaciones -->
          <div 
            ref="tableContainer"
            class="flex-1 overflow-auto border rounded-lg cursor-grab relative"
            style="scrollbar-gutter: stable"
            @mousedown="handleMouseDown"
            @mousemove="handleMouseMove"
            @mouseup="handleMouseUp"
            @mouseleave="handleMouseUp"
          >
            <table class="table-fixed border-collapse min-w-full text-[9px]">
              <thead>
                <tr>
                  <th class="p-1 bg-slate-100 border sticky left-0 top-0 z-20 min-w-[30px] shadow-[2px_2px_2px_rgba(0,0,0,0.05)]">
                    Esf/Cil
                  </th>
                  <th 
                    v-for="cylindrical in cylindricalRange" 
                    :key="cylindrical"
                    class="p-1 bg-slate-100 border sticky top-0 z-10 min-w-[30px]"
                  >
                    {{ formatNumber(cylindrical) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="spherical in sphericalRange" :key="spherical">
                  <th class="p-1 bg-slate-100 border sticky left-0 z-10 min-w-[30px] shadow-[2px_0_2px_rgba(0,0,0,0.05)]">
                    {{ formatNumber(spherical) }}
                  </th>
                  <td
                    v-for="cylindrical in cylindricalRange"
                    :key="cylindrical"
                    class="border p-1 text-center relative group h-4"
                    :class="{
                      'bg-green-100': isAvailable(spherical, cylindrical),
                      'bg-red-50': !isAvailable(spherical, cylindrical)
                    }"
                  >
                    <span 
                      v-if="isAvailable(spherical, cylindrical)" 
                      class="inline-block w-0.5 h-0.5 rounded-full bg-green-500"
                    />
                    <span 
                      v-else
                      class="inline-block w-0.5 h-0.5 rounded-full bg-red-300"
                    />
                    <!-- Tooltip -->
                    <div
                      class="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30"
                      :class="{
                        'bg-slate-800': isAvailable(spherical, cylindrical),
                        'bg-red-500': !isAvailable(spherical, cylindrical)
                      }"
                    >
                      {{ formatGraduation(spherical, cylindrical) }}
                      <div 
                        class="absolute top-full left-1/2 -translate-x-1/2 -mt-px border-4 border-transparent"
                        :class="{
                          'border-t-slate-800': isAvailable(spherical, cylindrical),
                          'border-t-red-500': !isAvailable(spherical, cylindrical)
                        }"
                      ></div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Footer -->
        <div class="flex justify-end p-4 border-t">
          <button
            @click="emit('close')"
            class="px-3 py-1 bg-slate-100 text-slate-700 rounded hover:bg-slate-200 text-sm"
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Hide scrollbar but keep functionality */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* Scrollbar styles */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
  border: 2px solid #f1f5f9;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Firefox */
* {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;
}
</style>